<template>
  <div class="cfdi-preview">

    <!-- ── Header strip ── -->
    <div class="cfdi-preview-header" :class="`cfdi-type-${typeClass}`">
      <div class="cfdi-preview-header-left">
        <span class="cfdi-type-badge">{{ typeLabel }}</span>
        <span class="cfdi-folio" v-if="cfdi.serie || cfdi.folio">
          {{ [cfdi.serie, cfdi.folio].filter(Boolean).join('-') }}
        </span>
      </div>
      <div class="cfdi-preview-header-right">
        <span class="cfdi-status-badge" :class="`cfdi-status-${statusClass}`">
          {{ cfdi.estado || '–' }}
        </span>
        <button
          v-if="showStatusRefresh"
          type="button"
          class="cfdi-status-refresh-btn"
          :disabled="refreshingStatus || statusRefreshDisabled"
          @click="$emit('request-status-refresh')"
          :title="statusRefreshTitle"
          aria-label="Solicitar actualización SAT"
        >
          <i class="fa" :class="refreshingStatus ? 'fa-spinner fa-spin' : 'fa-refresh'"></i>
        </button>
        <span class="cfdi-preview-date">{{ formatDate(cfdi.fecha) }}</span>
      </div>
    </div>

    <!-- ── Parties ── -->
    <div class="cfdi-preview-parties">
      <div class="cfdi-party">
        <div class="cfdi-party-role">Emitido por</div>
        <div class="cfdi-party-name">{{ emisor.nombre || '–' }}</div>
        <div class="cfdi-party-rfc">{{ emisor.rfc || '–' }}</div>
        <div class="cfdi-party-detail" v-if="emisor.regimen_fiscal">
          {{ emisor.regimen_fiscal }} – {{ regimenNombre(emisor.regimen_fiscal) }}
        </div>
      </div>

      <div class="cfdi-party-sep">
        <i class="fa fa-arrow-right"></i>
      </div>

      <div class="cfdi-party">
        <div class="cfdi-party-role">Emitido a</div>
        <div class="cfdi-party-name">{{ receptor.nombre || '–' }}</div>
        <div class="cfdi-party-rfc">{{ receptor.rfc || '–' }}</div>
        <div class="cfdi-party-detail" v-if="receptor.uso_cfdi">
          Uso: {{ receptor.uso_cfdi }} – {{ usoCfdiNombre(receptor.uso_cfdi) }}
        </div>
      </div>
    </div>

    <!-- ── UUID + metadata ── -->
    <div class="cfdi-preview-meta">
      <div class="cfdi-meta-item cfdi-meta-uuid">
        <span class="cfdi-meta-label">UUID</span>
        <span class="cfdi-meta-value">{{ cfdi.uuid || '–' }}</span>
      </div>
      <div class="cfdi-meta-grid">
        <div class="cfdi-meta-item" v-if="cfdi.moneda">
          <span class="cfdi-meta-label">Moneda</span>
          <span class="cfdi-meta-value">{{ cfdi.moneda }}</span>
        </div>
        <div class="cfdi-meta-item" v-if="cfdi.moneda && cfdi.moneda !== 'MXN' && cfdi.tipo_cambio">
          <span class="cfdi-meta-label">Tipo de cambio</span>
          <span class="cfdi-meta-value">{{ fmtTipoCambio(cfdi.tipo_cambio) }} MXN</span>
        </div>
        <div class="cfdi-meta-item" v-if="cfdi.forma_pago">
          <span class="cfdi-meta-label">Forma de pago</span>
          <span class="cfdi-meta-value">{{ cfdi.forma_pago }} – {{ formaPagoNombre(cfdi.forma_pago) }}</span>
        </div>
        <div class="cfdi-meta-item" v-if="cfdi.metodo_pago">
          <span class="cfdi-meta-label">Método de pago</span>
          <span class="cfdi-meta-value">{{ cfdi.metodo_pago }} – {{ metodoPagoNombre(cfdi.metodo_pago) }}</span>
        </div>
        <div class="cfdi-meta-item" v-if="cfdi.lugar_expedicion">
          <span class="cfdi-meta-label">Lugar de expedición</span>
          <span class="cfdi-meta-value">{{ cfdi.lugar_expedicion }}</span>
        </div>
        <div class="cfdi-meta-item" v-if="cfdi.condiciones_de_pago">
          <span class="cfdi-meta-label">Condiciones de pago</span>
          <span class="cfdi-meta-value">{{ cfdi.condiciones_de_pago }}</span>
        </div>
      </div>
    </div>

    <!-- ── Conceptos (I, E, T) ── -->
    <template v-if="safeConceptos.length > 0">
      <div class="cfdi-preview-section-title">Conceptos</div>
      <div class="cfdi-preview-section-body">
        <div class="table-responsive">
          <table class="table table-sm cfdi-preview-table">
            <thead>
              <tr>
                <th>Descripción</th>
                <th class="text-center">Cant.</th>
                <th class="text-end">Valor Unitario</th>
                <th class="text-end">Descuento</th>
                <th class="text-end">Importe</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in safeConceptos" :key="i">
                <td>
                  <div class="concepto-desc">{{ c.descripcion }}</div>
                  <div class="concepto-clave" v-if="c.clave_prod_serv || c.clave_unidad">
                    {{ c.clave_prod_serv }}<template v-if="c.clave_prod_serv && c.clave_unidad"> · </template>{{ c.clave_unidad }}<template v-if="c.clave_unidad && claveUnidadNombre(c.clave_unidad)"> ({{ claveUnidadNombre(c.clave_unidad) }})</template>
                  </div>
                </td>
                <td class="text-center">{{ c.cantidad }}</td>
                <td class="text-end">{{ fmtCurrency(c.valor_unitario) }}</td>
                <td class="text-end">{{ Number(c.descuento) > 0 ? fmtCurrency(c.descuento) : '–' }}</td>
                <td class="text-end">{{ fmtCurrency(c.importe) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- ── Totales ── -->
    <div class="cfdi-preview-totals">
      <div class="cfdi-total-row">
        <span>Subtotal</span>
        <span>{{ fmtCurrency(cfdi.sub_total) }}</span>
      </div>
      <div class="cfdi-total-row" v-if="Number(cfdi.descuento) > 0">
        <span>Descuento</span>
        <span class="text-danger">– {{ fmtCurrency(cfdi.descuento) }}</span>
      </div>
      <div class="cfdi-total-row" v-if="Number(totales.iva) > 0">
        <span>IVA {{ taxPctLabel(totales.iva_tasa, 16) }}</span>
        <span>{{ fmtCurrency(totales.iva) }}</span>
      </div>
      <div class="cfdi-total-row" v-if="Number(totales.iva_8) > 0">
        <span>IVA {{ taxPctLabel(0.08, 8) }}</span>
        <span>{{ fmtCurrency(totales.iva_8) }}</span>
      </div>
      <div class="cfdi-total-row" v-if="Number(totales.ieps) > 0">
        <span>IEPS</span>
        <span>{{ fmtCurrency(totales.ieps) }}</span>
      </div>
      <div class="cfdi-total-row" v-if="Number(totales.ret_iva) > 0">
        <span>Ret. IVA {{ taxPctLabel(totales.ret_iva_tasa, null) }}</span>
        <span class="text-danger">– {{ fmtCurrency(totales.ret_iva) }}</span>
      </div>
      <div class="cfdi-total-row" v-if="Number(totales.ret_isr) > 0">
        <span>Ret. ISR</span>
        <span class="text-danger">– {{ fmtCurrency(totales.ret_isr) }}</span>
      </div>
      <div class="cfdi-total-row cfdi-total-final">
        <span>Total</span>
        <span>{{ fmtCurrency(cfdi.total) }}</span>
      </div>
    </div>

    <template v-if="isPayrollCfdi && hasNominaData">
      <br />
      <div class="cfdi-preview-section-title">Información de Nómina</div>
      <div class="cfdi-preview-section-body">
        <div class="cfdi-party-role" style="margin-bottom: 6px;">Información general</div>
        <div class="table-responsive">
          <table class="table table-sm cfdi-preview-table">
            <tbody>
              <tr>
                <th>Versión Nómina</th>
                <td>{{ nomina.version_nomina || '–' }}</td>
                <th>Tipo Nómina</th>
                <td>{{ tipoNominaLabel(nomina.tipo_nomina) }}</td>
              </tr>
              <tr>
                <th>Fecha de pago</th>
                <td>{{ formatDate(nomina.fecha_pago) }}</td>
                <th>No. días pagados</th>
                <td>{{ Number(nomina.num_dias_pagados || 0).toFixed(2) }}</td>
              </tr>
              <tr>
                <th>Periodo</th>
                <td colspan="3">{{ formatDate(nomina.fecha_inicial_pago) }} a {{ formatDate(nomina.fecha_final_pago) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="cfdi-party-role" style="margin: 10px 0 6px;">Montos</div>
        <div class="table-responsive">
          <table class="table table-sm cfdi-preview-table">
            <tbody>
              <tr>
                <th>Total percepciones</th>
                <td>{{ fmtCurrency(nomina.total_percepciones) }}</td>
                <th>Total deducciones</th>
                <td>{{ fmtCurrency(nomina.total_deducciones) }}</td>
              </tr>
              <tr>
                <th>Total otros pagos</th>
                <td>{{ fmtCurrency(nomina.total_otros_pagos) }}</td>
                <th>Total pagado</th>
                <td>{{ fmtCurrency(nomina.total_pagado) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- ── Información de Pago/Relación ── -->
    <template v-if="relatedData && relationType === 'payment'">
      <br />
      <div class="cfdi-preview-section-title">{{ paymentRelatedTitle }}</div>
      <div class="cfdi-preview-section-body">
        <div class="table-responsive">
          <table class="table table-sm cfdi-preview-table">
            <thead>
              <tr>
                <th class="text-center">Parcialidad</th>
                <th>Fecha</th>
                <th class="text-end">Importe pagado</th>
                <th class="text-end">Saldo insoluto</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(partiality, idx) in (relatedData.partialities || [])" :key="idx">
                <td class="text-center">#{{ partiality.parcialidad }}</td>
                <td>{{ formatDate(partiality.fecha) }}</td>
                <td class="text-end">{{ fmtCurrency(partiality.importe_pagado) }}</td>
                <td class="text-end">{{ fmtCurrency(partiality.saldo_insoluto) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <template v-if="relatedData && relationType === 'relation'">
      <br />
      <div class="cfdi-preview-section-title cfdi-preview-section-title--with-meta">
        <span>{{ relationRelatedTitle }}</span>
        <span v-if="contextRelationTypeLabel" class="cfdi-section-meta">{{ contextRelationTypeLabel }}</span>
      </div>
      <div class="cfdi-preview-section-body">
        <div class="table-responsive">
          <table class="table table-sm cfdi-preview-table">
            <thead>
              <tr>
                <th>UUID</th>
                <th>Fecha</th>
                <th class="text-end">Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(relation, idx) in (relatedData.relations || [])" :key="idx">
                <td>
                  <span class="cfdi-relation-uuid">{{ relation.related_uuid || '–' }}</span>
                </td>
                <td>{{ formatDate(relation.related_fecha) }}</td>
                <td class="text-end">{{ fmtCurrency(relation.related_total) }}</td>
                <td>{{ relation.related_estado || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- ── CFDIs donde este CFDI apunta a cfdi_relation_id ── -->
    <template v-if="hasCfdisRelacionadosDirectos">
      <br />
      <div class="cfdi-preview-section-title cfdi-preview-section-title--with-meta">
        <span>CFDIs Relacionados</span>
        <span v-if="directRelationTypeLabel" class="cfdi-section-meta">{{ directRelationTypeLabel }}</span>
      </div>
      <div class="cfdi-preview-section-body">
        <div class="table-responsive">
          <table class="table table-sm cfdi-preview-table">
            <thead>
              <tr>
                <th>UUID</th>
                <th>Fecha</th>
                <th class="text-end">Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cfdiRel, idx) in cfdisRelacionadosDirectos" :key="`cfdi-rel-dir-${idx}`">
                <td>
                  <span class="cfdi-relation-uuid">{{ cfdiRel.related_uuid || '–' }}</span>
                </td>
                <td>{{ formatDate(cfdiRel.related_fecha) }}</td>
                <td class="text-end">{{ fmtCurrency(cfdiRel.related_total) }}</td>
                <td>{{ cfdiRel.related_estado || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- ── CFDIs que apuntan a este CFDI en cfdi_relation_id ── -->
    <template v-if="hasCfdisQueLoRelacionan">
      <br />
      <div class="cfdi-preview-section-title cfdi-preview-section-title--with-meta">
        <span>CFDIs que lo relacionan</span>
        <span v-if="incomingRelationTypeLabel" class="cfdi-section-meta">{{ incomingRelationTypeLabel }}</span>
      </div>
      <div class="cfdi-preview-section-body">
        <div class="table-responsive">
          <table class="table table-sm cfdi-preview-table">
            <thead>
              <tr>
                <th>UUID</th>
                <th>Fecha</th>
                <th class="text-end">Total</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(cfdiRel, idx) in cfdisQueLoRelacionan" :key="`cfdi-rel-inv-${idx}`">
                <td>
                  <span class="cfdi-relation-uuid">{{ cfdiRel.related_uuid || '–' }}</span>
                </td>
                <td>{{ formatDate(cfdiRel.related_fecha) }}</td>
                <td class="text-end">{{ fmtCurrency(cfdiRel.related_total) }}</td>
                <td>{{ cfdiRel.related_estado || '–' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>

    <!-- ── Documentos/Pagos Relacionados (para cuando se abre directo o desde cfdi relacionado) ── -->
    <template v-if="hasDocumentosRelacionados && (navigationSource === 'direct' || navigationSource === 'cfdi_relacionado')">
      <br />
      <div class="cfdi-preview-section-title">{{ documentosRelacionadosTitle }}</div>
      <div class="cfdi-preview-section-body">
        <div
          v-if="showPueWithRelatedPaymentsWarning"
          class="alert alert-warning alert-sm cfdi-anomaly-alert"
          role="alert"
        >
          Este CFDI tiene metodo de pago PUE y tambien pagos relacionados. Esto puede indicar una anomalia de captura o timbrado.
        </div>
        <div class="table-responsive">
          <table class="table table-sm cfdi-preview-table">
            <thead>
              <tr v-if="isPaymentCfdi">
                <th>UUID</th>
                <th>Fecha</th>
                <th class="text-end">Importe</th>
                <th class="text-end">Monto Pagado</th>
              </tr>
              <tr v-else>
                <th>UUID Pago</th>
                <th>Fecha de pago</th>
                <th>Fecha de emision</th>
                <th class="text-center">Parcialidad</th>
                <th class="text-end">Monto pagado</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="isPaymentCfdi">
                <tr v-for="(docRel, idx) in (documentosRelacionados.documents || [])" :key="`doc-rel-${idx}`">
                  <td>
                    <span class="cfdi-relation-uuid">{{ docRel.uuid || '–' }}</span>
                  </td>
                  <td>{{ formatDate(docRel.fecha) }}</td>
                  <td class="text-end">{{ fmtCurrency(docRel.total) }}</td>
                  <td class="text-end">{{ fmtCurrency(docRel.monto_pagado) }}</td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="(docRel, idx) in (documentosRelacionados.documents || [])" :key="`doc-rel-${idx}`">
                  <td>
                    <span class="cfdi-relation-uuid">{{ docRel.uuid || '–' }}</span>
                  </td>
                  <td>{{ formatDate(docRel.fecha_pago || docRel.fecha) }}</td>
                  <td>{{ formatDate(docRel.fecha_emision || docRel.fecha) }}</td>
                  <td class="text-center">{{ docRel.parcialidades || docRel.parcialidad || '–' }}</td>
                  <td class="text-end">{{ fmtCurrency(docRel.monto_pagado) }}</td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </template>

  </div>
</template>

<script>
const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

const REGIMEN_FISCAL = {
  '601': 'General de Ley Personas Morales',
  '603': 'Personas Morales con Fines no Lucrativos',
  '605': 'Sueldos y Salarios e Ingresos Asimilados a Salarios',
  '606': 'Arrendamiento',
  '607': 'Régimen de Enajenación o Adquisición de Bienes',
  '608': 'Demás ingresos',
  '609': 'Consolidación',
  '610': 'Residentes en el Extranjero sin Establecimiento Permanente en México',
  '611': 'Ingresos por Dividendos (socios y accionistas)',
  '612': 'Personas Físicas con Actividades Empresariales y Profesionales',
  '614': 'Ingresos por intereses',
  '615': 'Régimen de los ingresos por obtención de premios',
  '616': 'Sin obligaciones fiscales',
  '620': 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos',
  '621': 'Incorporación Fiscal',
  '622': 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras',
  '623': 'Opcional para Grupos de Sociedades',
  '624': 'Coordinados',
  '625': 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas',
  '626': 'Régimen Simplificado de Confianza',
};

const USO_CFDI = {
  'G01': 'Adquisición de mercancias',
  'G02': 'Devoluciones, descuentos o bonificaciones',
  'G03': 'Gastos en general',
  'I01': 'Construcciones',
  'I02': 'Mobilario y equipo de oficina por inversiones',
  'I03': 'Equipo de transporte',
  'I04': 'Equipo de computo y accesorios',
  'I05': 'Dados, troqueles, moldes, matrices y herramental',
  'I06': 'Comunicaciones telefónicas',
  'I07': 'Comunicaciones satelitales',
  'I08': 'Otra maquinaria y equipo',
  'D01': 'Honorarios médicos, dentales y gastos hospitalarios',
  'D02': 'Gastos médicos por incapacidad o discapacidad',
  'D03': 'Gastos funerales',
  'D04': 'Donativos',
  'D05': 'Intereses reales efectivamente pagados por créditos hipotecarios (casa habitación)',
  'D06': 'Aportaciones voluntarias al SAR',
  'D07': 'Primas por seguros de gastos médicos',
  'D08': 'Gastos de transportación escolar obligatoria',
  'D09': 'Depósitos en cuentas para el ahorro, primas que tengan como base planes de pensiones',
  'D10': 'Pagos por servicios educativos (colegiaturas)',
  'S01': 'Sin efectos fiscales',
  'CP01': 'Pagos',
  'CN01': 'Nómina',
};

const FORMA_PAGO = {
  '01': 'Efectivo',
  '02': 'Cheque nominativo',
  '03': 'Transferencia electrónica de fondos',
  '04': 'Tarjeta de crédito',
  '05': 'Monedero electrónico',
  '06': 'Dinero electrónico',
  '08': 'Vales de despensa',
  '12': 'Dación en pago',
  '13': 'Pago por subrogación',
  '14': 'Pago por consignación',
  '15': 'Condonación',
  '17': 'Compensación',
  '23': 'Novación',
  '24': 'Confusión',
  '25': 'Remisión de deuda',
  '26': 'Prescripción o caducidad',
  '27': 'A satisfacción del acreedor',
  '28': 'Tarjeta de débito',
  '29': 'Tarjeta de servicios',
  '30': 'Aplicación de anticipos',
  '31': 'Intermediario pagos',
  '99': 'Por definir',
};

const METODO_PAGO = {
  'PUE': 'Pago en una sola exhibición',
  'PPD': 'Pago en parcialidades o diferido',
};

const CLAVE_UNIDAD = {
  'ACT': 'Actividad',
  'E48': 'Unidad de servicio',
  'H87': 'Pieza',
  'EA':  'Elemento',
  'KGM': 'Kilogramo',
  'GRM': 'Gramo',
  'MTR': 'Metro',
  'CMT': 'Centímetro',
  'MMT': 'Milímetro',
  'KMT': 'Kilómetro',
  'MTK': 'Metro cuadrado',
  'MTQ': 'Metro cúbico',
  'LTR': 'Litro',
  'MLT': 'Mililitro',
  'HUR': 'Hora',
  'MIN': 'Minuto',
  'DAY': 'Día',
  'MON': 'Mes',
  'ANN': 'Año',
  'SET': 'Juego',
  'PR':  'Par',
  'DZN': 'Docena',
  'XBX': 'Caja',
  'XPK': 'Paquete',
  'XKG': 'Kit',
  'XBG': 'Bolsa',
  'XRO': 'Rollo',
  'XPP': 'Pieza por pliego',
  'XTN': 'Tonelada',
  'XLT': 'Lote',
  'XPB': 'Paquete a granel',
  'ONE': 'Uno',
};

const TYPE_MAP = {
  I: { label: 'Factura',               cls: 'ingreso'    },
  E: { label: 'Nota de Crédito',       cls: 'egreso'     },
  P: { label: 'Complemento de Pago',   cls: 'pago'       },
  N: { label: 'Recibo de Nómina',      cls: 'nomina'     },
  T: { label: 'Carta Porte / Traslado',cls: 'traslado'   },
};

const TIPO_RELACION_CFDI = {
  '01': 'Nota de credito de los documentos relacionados',
  '02': 'Nota de debito de los documentos relacionados',
  '03': 'Devolucion de mercancia sobre facturas o traslados previos',
  '04': 'Sustitucion de CFDI previos',
  '05': 'Traslados de mercancias facturados previamente',
  '06': 'Factura generada por los traslados previos',
  '07': 'CFDI por aplicacion de anticipo',
};

export default {
  name: 'CfdiPreview',
  emits: ['request-status-refresh'],
  props: {
    cfdi:                   { type: Object, default: () => ({}) },
    emisor:                 { type: Object, default: () => ({}) },
    receptor:               { type: Object, default: () => ({}) },
    conceptos:              { type: Array,  default: () => []   },
    totales:                { type: Object, default: () => ({}) },
    nomina:                 { type: Object, default: null       },
    relatedData:            { type: Object, default: null       },
    relationType:           { type: String, default: ''         },
    cfdisRelacionados:      { type: Object, default: null       },
    documentosRelacionados: { type: Object, default: null       },
    navigationSource:       { type: String, default: 'direct'   },
    showStatusRefresh:      { type: Boolean, default: false     },
    refreshingStatus:       { type: Boolean, default: false     },
    statusRefreshDisabled:  { type: Boolean, default: false     },
    statusRefreshTitle:     { type: String,  default: 'Solicitar actualización SAT' },
  },
  computed: {
    safeConceptos() {
      return Array.isArray(this.conceptos) ? this.conceptos : [];
    },
    typeClass() {
      const t = String(this.cfdi?.tipo_de_comprobante || '').toUpperCase();
      return TYPE_MAP[t]?.cls || 'default';
    },
    typeLabel() {
      const t = String(this.cfdi?.tipo_de_comprobante || '').toUpperCase();
      return TYPE_MAP[t]?.label || 'CFDI';
    },
    statusClass() {
      const s = String(this.cfdi?.estado || '').toUpperCase();
      if (s === 'VIGENTE')    return 'vigente';
      if (s === 'CANCELADO')  return 'cancelado';
      return 'unknown';
    },
    cfdisRelacionadosDirectos() {
      const direct = this.cfdisRelacionados?.cfdis_relacionados;
      if (Array.isArray(direct)) return direct;

      const legacy = this.cfdisRelacionados?.relations;
      if (!Array.isArray(legacy)) return [];
      return legacy.filter((row) => row?.direction !== 'from_relation_id');
    },
    cfdisQueLoRelacionan() {
      const inverse = this.cfdisRelacionados?.cfdis_que_lo_relacionan;
      if (Array.isArray(inverse)) return inverse;

      const legacy = this.cfdisRelacionados?.relations;
      if (!Array.isArray(legacy)) return [];
      return legacy.filter((row) => row?.direction === 'from_relation_id');
    },
    hasCfdisRelacionadosDirectos() {
      return this.cfdisRelacionadosDirectos.length > 0;
    },
    hasCfdisQueLoRelacionan() {
      return this.cfdisQueLoRelacionan.length > 0;
    },
    hasDocumentosRelacionados() {
      return Array.isArray(this.documentosRelacionados?.documents)
        && this.documentosRelacionados.documents.length > 0;
    },
    relationRelatedTitle() {
      return this.navigationSource === 'cfdi_relacionado'
        ? 'CFDIs que lo relacionan'
        : 'CFDIs Relacionados';
    },
    paymentRelatedTitle() {
      return this.navigationSource === 'documento_relacionado'
        ? 'Información del Pago Relacionado'
        : 'Parcialidades de este pago';
    },
    documentosRelacionadosTitle() {
      return String(this.cfdi?.tipo_de_comprobante || '').toUpperCase() === 'P'
        ? 'Documentos Relacionados'
        : 'Información del Pago Relacionado';
    },
    isPaymentCfdi() {
      return String(this.cfdi?.tipo_de_comprobante || '').toUpperCase() === 'P';
    },
    showPueWithRelatedPaymentsWarning() {
      const metodoPago = String(this.cfdi?.metodo_pago || '').trim().toUpperCase();
      return !this.isPaymentCfdi && metodoPago === 'PUE' && this.hasDocumentosRelacionados;
    },
    contextRelationTypeLabel() {
      return this.buildRelationTypeLabel(this.relatedData?.relations || []);
    },
    directRelationTypeLabel() {
      return this.buildRelationTypeLabel(this.cfdisRelacionadosDirectos);
    },
    incomingRelationTypeLabel() {
      return this.buildRelationTypeLabel(this.cfdisQueLoRelacionan);
    },
    isPayrollCfdi() {
      return String(this.cfdi?.tipo_de_comprobante || '').toUpperCase() === 'N';
    },
    hasNominaData() {
      return !!(this.nomina && typeof this.nomina === 'object');
    },
  },
  methods: {
    regimenNombre(code) {
      return REGIMEN_FISCAL[String(code || '').trim()] || '';
    },
    usoCfdiNombre(code) {
      return USO_CFDI[String(code || '').trim().toUpperCase()] || '';
    },
    formaPagoNombre(code) {
      return FORMA_PAGO[String(code || '').trim()] || '';
    },
    metodoPagoNombre(code) {
      return METODO_PAGO[String(code || '').trim().toUpperCase()] || '';
    },
    tipoNominaLabel(code) {
      const value = String(code || '').trim().toUpperCase();
      if (value === 'O') return 'O - Ordinaria';
      if (value === 'E') return 'E - Extraordinaria';
      return value || '–';
    },
    claveUnidadNombre(code) {
      return CLAVE_UNIDAD[String(code || '').trim().toUpperCase()] || '';
    },
    taxPctLabel(tasa, fallbackPct) {
      const n = Number(tasa);
      if (!isNaN(n) && n > 0) {
        const pct = n <= 1 ? n * 100 : n;
        return `${parseFloat(pct.toFixed(2))}%`;
      }
      if (fallbackPct !== null && fallbackPct !== undefined) {
        return `${fallbackPct}%`;
      }
      return '';
    },
    fmtTipoCambio(value) {
      const n = Number(String(value ?? 0).replace(/,/g, ''));
      if (isNaN(n) || n === 0) return '–';
      return new Intl.NumberFormat('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      }).format(n);
    },
    formatDate(value) {
      if (!value) return '–';
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return `${d.getDate()} ${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`;
    },
    fmtCurrency(value) {
      const n = Number(String(value ?? 0).replace(/,/g, ''));
      const formatted = new Intl.NumberFormat('es-MX', {
        style: 'currency', currency: 'MXN',
        minimumFractionDigits: 2, maximumFractionDigits: 2,
      }).format(isNaN(n) ? 0 : n);
      return formatted.replace(/^\$/, '$ ').replace(/^-\$/, '-$ ');
    },
    relationTypeName(code) {
      return TIPO_RELACION_CFDI[String(code || '').trim()] || '';
    },
    buildRelationTypeLabel(items) {
      if (!Array.isArray(items) || items.length === 0) return '';

      const codes = [...new Set(items.map((row) => String(row?.tipo_relacion || '').trim()).filter(Boolean))];
      if (codes.length === 0) return '';

      if (codes.length > 1) {
        return 'Tipo de relacion: multiple';
      }

      const code = codes[0];
      const name = this.relationTypeName(code);
      return name ? `${code} - ${name}` : code;
    },
  },
};
</script>

<style scoped>
.cfdi-preview {
  font-size: 12px;
  color: var(--theme-font-color);
}

/* ── Header ── */
.cfdi-preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 8px 8px 0 0;
  gap: 12px;
  margin-bottom: 0;
}

.cfdi-preview-header-left,
.cfdi-preview-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.cfdi-type-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  background: rgba(255,255,255,.22);
  color: #fff;
}

.cfdi-folio {
  font-size: 11px;
  font-weight: 600;
  color: rgba(255,255,255,.85);
}

.cfdi-preview-date {
  font-size: 11px;
  color: rgba(255,255,255,.8);
}

.cfdi-status-badge {
  padding: 2px 9px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: .04em;
}

.cfdi-status-refresh-btn {
  width: 24px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, .35);
  background: rgba(255, 255, 255, .15);
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background .15s ease, opacity .15s ease;
}

.cfdi-status-refresh-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, .28);
}

.cfdi-status-refresh-btn:disabled {
  opacity: .7;
  cursor: not-allowed;
}

.cfdi-status-vigente   { background: rgba(255,255,255,.25); color: #fff; }
.cfdi-status-cancelado { background: rgba(180,35,24,.8);    color: #fff; }
.cfdi-status-unknown   { background: rgba(255,255,255,.15); color: rgba(255,255,255,.8); }

/* Type colours */
.cfdi-type-ingreso   { background: linear-gradient(100deg, var(--theme-deafult), #7366ff); }
.cfdi-type-egreso    { background: linear-gradient(100deg, #f7316488, #f73164); }
.cfdi-type-pago      { background: linear-gradient(100deg, #0f766e, #14b8a6); }
.cfdi-type-nomina    { background: linear-gradient(100deg, #1d4ed8, #3b82f6); }
.cfdi-type-traslado  { background: linear-gradient(100deg, #92400e, #d97706); }
.cfdi-type-default   { background: linear-gradient(100deg, #64748b, #94a3b8); }

/* ── Parties ── */
.cfdi-preview-parties {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border: 1px solid var(--sidebar-border);
  border-top: 0;
  background: var(--white);
}

.cfdi-party {
  flex: 1;
}

.cfdi-party-sep {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  padding-top: 18px;
  color: var(--theme-deafult);
  opacity: .5;
}

.cfdi-party-role {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--theme-deafult);
  margin-bottom: 4px;
}

.cfdi-party-name {
  font-size: 12px;
  font-weight: 600;
  color: var(--theme-font-color);
  line-height: 1.3;
}

.cfdi-party-rfc {
  font-size: 11px;
  color: var(--theme-font-color);
  opacity: .7;
  margin-top: 2px;
}

.cfdi-party-detail {
  font-size: 10px;
  color: var(--theme-font-color);
  opacity: .55;
  margin-top: 2px;
}

/* ── Meta ── */
.cfdi-preview-meta {
  padding: 10px 14px;
  border: 1px solid var(--sidebar-border);
  border-top: 0;
  background: rgba(115, 102, 255, 0.04);
}

.cfdi-meta-uuid {
  margin-bottom: 8px;
}

.cfdi-meta-uuid .cfdi-meta-value {
  font-family: monospace;
  font-size: 11px;
  word-break: break-all;
}

.cfdi-meta-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px 20px;
}

@media (max-width: 600px) {
  .cfdi-meta-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.cfdi-meta-item {
  display: flex;
  flex-direction: column;
}

.cfdi-meta-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .05em;
  color: var(--theme-font-color);
  opacity: .55;
}

.cfdi-meta-value {
  font-size: 11px;
  font-weight: 500;
  color: var(--theme-font-color);
}

/* ── Section title ── */
.cfdi-preview-section-title {
  padding: 8px 14px 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  color: var(--theme-deafult);
  border: 1px solid var(--sidebar-border);
  border-top: 0;
  background: var(--white);
}

/* ── Conceptos table ── */
.cfdi-preview-table {
  margin-bottom: 0;
  border-radius: 0;
}

.cfdi-preview-table th,
.cfdi-preview-table td {
  font-size: 11px;
  white-space: nowrap;
}

.concepto-desc {
  font-weight: 500;
  white-space: normal;
  line-height: 1.3;
}

.concepto-clave {
  font-size: 10px;
  opacity: .6;
  margin-top: 2px;
}

/* ── Totales ── */
.cfdi-preview-totals {
  border: 1px solid var(--sidebar-border);
  border-top: 0;
  border-radius: 0 0 8px 8px;
  padding: 10px 14px;
  background: var(--white);
}

.cfdi-total-row {
  display: flex;
  justify-content: space-between;
  padding: 3px 0;
  font-size: 11px;
  color: var(--theme-font-color);
}

.cfdi-total-final {
  border-top: 1px solid var(--sidebar-border);
  margin-top: 4px;
  padding-top: 8px;
  font-size: 13px;
  font-weight: 700;
}

.cfdi-preview-section-title {
  padding: 10px 14px 8px;
  font-size: 12px;
  font-weight: 700;
  color: var(--theme-font-color);
  border-left: 1px solid var(--sidebar-border);
  border-right: 1px solid var(--sidebar-border);
  border-bottom: 1px solid var(--sidebar-border);
  border-top: 1px solid var(--sidebar-border);
  margin-top: 0;
  background-color: var(--white);
}

.cfdi-preview-section-title--with-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.cfdi-section-meta {
  font-size: 11px;
  font-weight: 600;
  color: var(--theme-font-color);
  opacity: .78;
  text-transform: none;
}

.cfdi-preview-section-body {
  border-left: 1px solid var(--sidebar-border);
  border-right: 1px solid var(--sidebar-border);
  border-bottom: 1px solid var(--sidebar-border);
  padding: 10px 12px 12px;
  background: var(--white);
}

.cfdi-preview-table {
  margin-bottom: 0;
}

.cfdi-relation-uuid {
  font-family: monospace;
  font-size: 10px;
  word-break: break-all;
}

.cfdi-anomaly-alert {
  margin-bottom: 10px;
  padding: 8px 10px;
  font-size: 11px;
}
</style>
